import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './StrengthSelection.css';

const StrengthSelection = () => {
    const navigate = useNavigate();
    const [userInput, setUserInput] = useState('');

    // Strength workout presets (from Android code)
    const strengthWorkouts = [
        { code: "$", name: "chest Workout" },
        { code: "x$", name: "back chest Workout" },
        { code: "&+$", name: "shoulder abs chest Workout" },
        { code: "*$", name: "arm chest Workout" },
        { code: "(=$", name: "bicep leg chest Workout" },
        { code: ")$", name: "tricep chest Workout" },
        { code: "-$", name: "forearm chest Workout" },
        { code: "+$+", name: "abs chest abs Workout" },
        { code: "=[$=]", name: "leg quads chest leg hamstring Workout" },
        { code: "[$][$]", name: "quads chest hamstring quads chest hamstring Workout" },
        { code: "]$[", name: "hamstring chest quads Workout" },
        { code: "{$}", name: "glute chest calves Workout" },
        { code: "}$:", name: "calves chest trap Workout" },
        { code: ":$;", name: "trap chest obliques Workout" },
        { code: ";$()", name: "obliques chest bicep tricep Workout" },
        { code: "|$()", name: "hip chest bicep tricep Workout" },
        { code: "<$>=", name: "thigh chest overall leg Workout" },
        { code: "]=$=[", name: "hamstring leg chest leg quads Workout" },
        { code: "{=$:}", name: "glute leg chest trap calves Workout" },
        { code: "}:$:", name: "calves trap chest trap Workout" },
        { code: "::$;", name: "trap trap chest obliques Workout" },
        { code: ";$:()", name: "obliques chest trap bicep tricep Workout" },
        { code: "|$[()", name: "hip chest quad bicep tricep Workout" },
        { code: "<$[]>=", name: "thigh chest quad hamstring overall leg Workout" },
        { code: ">[$[<=", name: "overall quad chest quad thigh leg Workout" }
    ];

    // Muscle groups mapping (from Android app)
    const muscleGroups = [
        { name: "chest", code: "$" },
        { name: "back", code: "x" },
        { name: "shoulder", code: "&" },
        { name: "arm", code: "*" },
        { name: "bicep", code: "(" },
        { name: "tricep", code: ")" },
        { name: "forearm", code: "-" },
        { name: "abs", code: "+" },
        { name: "leg", code: "=" },
        { name: "quad", code: "[" },
        { name: "hamstring", code: "]" },
        { name: "glute", code: "{" },
        { name: "calves", code: "}" },
        { name: "trap", code: ":" },
        { name: "obliques", code: ";" },
        { name: "hip", code: "|" },
        { name: "thigh", code: "<" },
        { name: "overall", code: ">" }
    ];

    const allowedChars = "$x&*()-+=[]{}:;|<>";

    // Handle adding character to input
    const addCharacter = (char) => {
        const newInput = userInput + char;
        const lastThreeChars = newInput.slice(-3);

        // Prevent three consecutive same characters
        if (lastThreeChars.length < 3 || !(lastThreeChars[0] === lastThreeChars[1] && lastThreeChars[1] === lastThreeChars[2])) {
            setUserInput(newInput);
        }
    };

    // Handle input change
    const handleInputChange = (e) => {
        const input = e.target.value.toLowerCase();
        const validInput = input.split('').filter(char => allowedChars.includes(char)).join('');

        // Check for three consecutive same characters
        let result = '';
        for (let i = 0; i < validInput.length; i++) {
            const char = validInput[i];
            const lastThreeChars = result.slice(-2) + char;
            if (lastThreeChars.length < 3 || !(lastThreeChars[0] === lastThreeChars[1] && lastThreeChars[1] === lastThreeChars[2])) {
                result += char;
            }
        }

        setUserInput(result);
    };

    // Handle preset workout selection
    const handlePresetSelect = (workoutCode) => {
        setUserInput(workoutCode);
    };

    // Handle next button with URL encoding fix
    const handleNext = () => {
        if (userInput.trim() !== '') {
            // Navigate with properly encoded workout code
            const encodedWorkoutCode = encodeURIComponent(userInput);
            navigate(`/strength-rep-count/${encodedWorkoutCode}`, {
                state: {
                    workoutCode: userInput, // Pass unencoded version in state
                    workoutType: 'strength'
                }
            });
        }
    };

    // Generate random background image
    const getRandomBackground = () => {
        const randomIndex = Math.floor(Math.random() * 88) + 1;
        return `url(/assets/images/v${randomIndex}.jpg)`;
    };

    return (
        <div
            className="strength-selection-container"
            style={{
                backgroundImage: getRandomBackground(),
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}
        >
            {/* Dark overlay */}
            <div className="strength-overlay"></div>

            {/* Fixed top bar */}
            <div className="strength-top-bar">
                <button
                    className="strength-back-button"
                    onClick={() => navigate('/')}
                >
                    ← Back
                </button>

                <input
                    type="text"
                    className="strength-input"
                    value={userInput}
                    onChange={handleInputChange}
                    placeholder="Enter strength code..."
                />

                <button
                    className="strength-next-button"
                    onClick={handleNext}
                    disabled={userInput.trim() === ''}
                >
                    Next 🏋️
                </button>
            </div>

            {/* Scrollable content */}
            <div className="strength-content">
                <div className="strength-header">
                    <h1>🏋️ STRENGTH TRAINING 🏋️</h1>
                    <p>Build maximum power and muscle mass!</p>
                </div>

                {/* Muscle group buttons */}
                <div className="muscle-groups-grid">
                    {muscleGroups.map((group) => (
                        <button
                            key={group.code}
                            className="muscle-group-button"
                            onClick={() => addCharacter(group.code)}
                        >
                            <span className="muscle-code">{group.code}</span>
                            <span className="muscle-name">{group.name}</span>
                        </button>
                    ))}
                </div>

                {/* Character selector */}
                <div className="character-selector">
                    <h3>Quick Character Input:</h3>
                    <div className="character-grid">
                        {allowedChars.split('').map((char) => (
                            <button
                                key={char}
                                className="character-button"
                                onClick={() => addCharacter(char)}
                            >
                                {char}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Preset strength workouts */}
                <div className="preset-workouts">
                    <h3>💪 Strength Building Presets 💪</h3>
                    <div className="preset-workouts-grid">
                        {strengthWorkouts.map((workout, index) => (
                            <div
                                key={`${workout.code}-${index}`}
                                className="preset-workout-card"
                                onClick={() => handlePresetSelect(workout.code)}
                            >
                                <div className="preset-code">{workout.code}</div>
                                <div className="preset-name">{workout.name}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="strength-instructions">
                    <p>🏋️ Heavy weights for maximum gains!</p>
                    <p>💪 Progressive overload and compound movements</p>
                    <p>🔥 Build strength, power, and muscle mass!</p>
                </div>
            </div>
        </div>
    );
};

export default StrengthSelection;